import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  LeaveTypeEnum,
  NationalityEnum,
  WorkerDepartmentEnum
} from '__generated__/globalTypes'

import { RootState } from '../../redux/slices'
import { Leave } from './lib'

export type FormFilterNationalityState = Record<NationalityEnum, boolean>
export type FormFilterLeaveTypeState = Record<LeaveTypeEnum, boolean>
export type FormFilterPackageDepartmentState = Record<
  WorkerDepartmentEnum,
  boolean
>

export type FormFilterState = FormFilterPackageDepartmentState &
  FormFilterLeaveTypeState &
  FormFilterNationalityState

export const initialFilterState: FormFilterState = {
  [WorkerDepartmentEnum.AIRCON]: true,
  [WorkerDepartmentEnum.HOME_CLEANING]: true,
  [WorkerDepartmentEnum.OFFICE_CLEANING]: true,
  [WorkerDepartmentEnum.CARPET_UPHOLSTERY]: true,
  [WorkerDepartmentEnum.HOME_BEAUTY]: true,
  [WorkerDepartmentEnum.CAR_WASH]: true,
  [WorkerDepartmentEnum.HANDYMAN]: true,
  [WorkerDepartmentEnum.BABYSITTER]: true,
  [WorkerDepartmentEnum.MASSAGE]: true,
  [WorkerDepartmentEnum.DEEP_CLEANING]: true,
  [WorkerDepartmentEnum.ELDER_CARE]: true,
  [WorkerDepartmentEnum.PEST_CONTROL]: true,
  [WorkerDepartmentEnum.PET_CARE]: true,
  [WorkerDepartmentEnum.SECURITY]: true,
  [LeaveTypeEnum.AL]: true,
  [LeaveTypeEnum.SL]: true,
  [LeaveTypeEnum.NPL]: true,
  [LeaveTypeEnum.HL]: true,
  [LeaveTypeEnum.CL]: true,
  [LeaveTypeEnum.OD]: true,
  [NationalityEnum.Bangladeshi]: true,
  [NationalityEnum.Burmese]: true,
  [NationalityEnum.Cambodian]: true,
  [NationalityEnum.Chinese]: true,
  [NationalityEnum.Indian]: true,
  [NationalityEnum.Indonesian]: true,
  [NationalityEnum.Malaysian]: true,
  [NationalityEnum.Singaporean]: true,
  [NationalityEnum.SriLankan]: true,
  [NationalityEnum.Thai]: true,
  [NationalityEnum.Vietnamese]: true,
  [NationalityEnum.Singapore_PR]: true
}

type State = {
  allLeaves: Leave[]
  displayedLeaves: Leave[]
  displayTabLeaves: Leave[]
  selectedTab: number
  filterState: FormFilterState
  leaveFormModal: null | string
  leaveDetailModal: null | string
  selectedLeave: Leave | null
  page: number
}

export const initialState: State = {
  allLeaves: [],
  displayedLeaves: [],
  displayTabLeaves: [],
  selectedTab: 0,
  filterState: initialFilterState,
  leaveFormModal: null,
  leaveDetailModal: null,
  selectedLeave: null,
  page: 0
}

const leaveSlice = createSlice({
  name: 'leave',
  initialState,
  reducers: {
    setLeaves(state, { payload: leavesData }: { payload: Leave[] }) {
      state.allLeaves = leavesData
    },
    setFilterState(
      state,
      { payload: filterState }: { payload: FormFilterState }
    ) {
      state.filterState = filterState
    },
    setSelectedLeaveTab(state, { payload: selectedTab }: { payload: number }) {
      state.selectedTab = selectedTab
    },
    setLeaveFormModal(state, { payload }: PayloadAction<null | string>) {
      state.leaveFormModal = payload
    },
    setLeaveDetailModal(state, { payload }: PayloadAction<null | string>) {
      state.leaveDetailModal = payload
    },
    setSelectedLeave(state, { payload }: PayloadAction<null | Leave>) {
      state.selectedLeave = payload
    },
    setPage(state, { payload: pageCounting }: { payload: number }) {
      state.page = pageCounting
    }
  }
})

export const selectedTabSelector = (state: RootState) => {
  return state.leaveReducer.selectedTab
}

export const displayedLeaveSelector = (state: RootState) =>
  state.leaveReducer.allLeaves

export const filterStateSelector = (state: RootState) => {
  return state.leaveReducer.filterState
}

export const leaveFormModalSelector = (state: RootState): null | string => {
  return state.leaveReducer.leaveFormModal
}

export const leaveDetailModalSelector = (state: RootState): null | string => {
  return state.leaveReducer.leaveDetailModal
}

export const selectedLeaveSelector = (state: RootState): null | Leave => {
  return state.leaveReducer.selectedLeave
}

export const pageSelector = (state: RootState): number =>
  state.leaveReducer.page

export const { name, actions, reducer } = leaveSlice
